import _, { isUndefined } from "lodash";
import querystring from "query-string";
import { matchPath } from "react-router-dom";
import { history } from "store";
import { handleApiAction, handlePromiseAction } from "utils/actions";
import { apiClient, setForwarderAgent, setOccmAgent, getCachedClient, handleNetworkResponse, occmClient } from 'utils/clients';
import externals from "utils/externals";
import { enableGAPageViewTracking, setFullStoryUserValues, setGAUserProperties } from "utils/ga";
import { getItem, isNewTenancyEnabled, removeItem, setItem } from "utils/localstorage";
import { trackTiming } from "utils/mrTracker";
import { isOrgAdmin } from "utils/permissions";
import { getLocalizedValue as t } from "../../../i18n";
import { appComponents as i18nNamespace } from "../../../i18n/namespace";
import { getSafeModeHeaders, isNcl } from "utils/app/utils";

const CATEGORY_MENU = {
    platform: "Platform",
    application: "Application",
    data_service: "Data Service"
}

// String value maps to roleInternalProperty field of API
const Roles = Object.freeze({
    ORGANIZATION_ADMIN : "ORGANIZATION_ADMIN",
    FOLDER_OR_PROJECT_ADMIN : "FOLDER_OR_PROJECT_ADMIN",
    SNAPCENTER_ADMIN : "SNAPCENTER_ADMIN",
    CLASSIFICATION_VIEWER : "CLASSIFICATION_VIEWER",
    STORAGE_ADMIN : "STORAGE_ADMIN",
    STORAGE_VIEWER : "STORAGE_VIEWER",
    KEYSTONE_VIEWER : "KEYSTONE_VIEWER",
    KEYSTONE_ADMIN : "KEYSTONE_ADMIN",
    SYSTEM_HEALTH_SPECIALIST : "SYSTEM_HEALTH_SPECIALIST",
    RANSOMWARE_PROTECTION_ADMIN : "RANSOMWARE_PROTECTION_ADMIN",
    RANSOMWARE_PROTECTION_VIEWER : "RANSOMWARE_PROTECTION_VIEWER",
    RANSOMWARE_RESILIENCE_USER_BEHAVIOR_ADMIN: "RANSOMWARE_RESILIENCE_USER_BEHAVIOR_ADMIN",
    RANSOMWARE_RESILIENCE_USER_BEHAVIOR_VIEWER: "RANSOMWARE_RESILIENCE_USER_BEHAVIOR_VIEWER",
    ONTAP_MEDIATOR_SETUP : "ONTAP_MEDIATOR_SETUP",
    FEDERATION_ADMIN: "FEDERATION_ADMIN",
    FEDERATION_VIEWER: "FEDERATION_VIEWER",
    DISASTER_RECOVERY_FAILOVER_ADMIN: "DISASTER_RECOVERY_FAILOVER_ADMIN",
    DISASTER_RECOVERY_ADMIN: "DISASTER_RECOVERY_ADMIN",
    DISASTER_RECOVERY_APPLICATION_ADMIN: "DISASTER_RECOVERY_APPLICATION_ADMIN",
    DISASTER_RECOVERY_VIEWER: "DISASTER_RECOVERY_VIEWER",
    BACKUP_ADMIN: "BACKUP_ADMIN",
    BACKUP_SUPER_ADMIN: "BACKUP_SUPER_ADMIN",
    BACKUP_VIEWER: "BACKUP_VIEWER",
    CLONE_ADMIN: "CLONE_ADMIN",
    RESTORE_ADMIN: "RESTORE_ADMIN",
    GCNV_ADMIN: "GCNV_ADMIN",
    GCNV_VIEWER: "GCNV_VIEWER",
    OPERATIONS_SUPPORT_ANALYST: "OPERATIONS_SUPPORT_ANALYST",
    PARTNERSHIP_ADMIN: "PARTNERSHIP_ADMIN",
    PARTNERSHIP_VIEWER: "PARTNERSHIP_VIEWER",
    ORGANIZATION_VIEWER: "ORGANIZATION_VIEWER",
    SUPER_ADMIN: "SUPER_ADMIN",
    SUPER_VIEWER: "SUPER_VIEWER",
    LICENSE_AND_SUBSCRIPTION_ADMIN: "LICENSE_AND_SUBSCRIPTION_ADMIN",
    LICENSE_AND_SUBSCRIPTION_VIEWER: "LICENSE_AND_SUBSCRIPTION_VIEWER",
    CONSOLE_AGENT_ADMIN: "CONSOLE_AGENT_ADMIN",
    DSP_ADMIN:"DSP_ADMIN",
    DSP_ANALYST:"DSP_ANALYST",
    DSP_VIEWER: "DSP_VIEWER",
    CVO_ADMIN:"CVO_ADMIN",
    CVO_VIEWER: "CVO_VIEWER",
    COMPLIANCE_ADMIN: "COMPLIANCE_ADMIN"
});


const SUPPORTED_ROLES = [
    Roles.ORGANIZATION_ADMIN,
    Roles.FOLDER_OR_PROJECT_ADMIN,
    Roles.SNAPCENTER_ADMIN,
    Roles.CLASSIFICATION_VIEWER,
    Roles.STORAGE_ADMIN,
    Roles.STORAGE_VIEWER,
    Roles.KEYSTONE_VIEWER,
    Roles.KEYSTONE_ADMIN,
    Roles.SYSTEM_HEALTH_SPECIALIST,
    Roles.RANSOMWARE_PROTECTION_ADMIN,
    Roles.RANSOMWARE_PROTECTION_VIEWER,
    Roles.RANSOMWARE_RESILIENCE_USER_BEHAVIOR_ADMIN,
    Roles.RANSOMWARE_RESILIENCE_USER_BEHAVIOR_VIEWER,
    Roles.ONTAP_MEDIATOR_SETUP,
    Roles.FEDERATION_ADMIN,
    Roles.FEDERATION_VIEWER,
    Roles.DISASTER_RECOVERY_FAILOVER_ADMIN,
    Roles.DISASTER_RECOVERY_ADMIN,
    Roles.DISASTER_RECOVERY_APPLICATION_ADMIN,
    Roles.DISASTER_RECOVERY_VIEWER,
    Roles.BACKUP_ADMIN,
    Roles.BACKUP_SUPER_ADMIN,
    Roles.BACKUP_VIEWER,
    Roles.CLONE_ADMIN,
    Roles.RESTORE_ADMIN,
    Roles.GCNV_ADMIN,
    Roles.GCNV_VIEWER,
    Roles.OPERATIONS_SUPPORT_ANALYST,
    Roles.PARTNERSHIP_ADMIN,
    Roles.PARTNERSHIP_VIEWER,
    Roles.ORGANIZATION_VIEWER,
    Roles.SUPER_ADMIN,
    Roles.SUPER_VIEWER,
    Roles.LICENSE_AND_SUBSCRIPTION_ADMIN,
    Roles.LICENSE_AND_SUBSCRIPTION_VIEWER,
    Roles.CONSOLE_AGENT_ADMIN,
    Roles.CVO_VIEWER,
    Roles.CVO_ADMIN,
    Roles.COMPLIANCE_ADMIN
];

// Roles enabled by feature flag NetApp.TenancyV4/enableRoles
const FEATURE_FLAGGED_ROLES = {
    "NetApp.DataSecurity/*": [ Roles.DSP_ADMIN, Roles.DSP_ANALYST, Roles.DSP_VIEWER]
}


// Returns supported roles based on feature flags
const getSupportedRoles = (features) => {
    let enabledRoles = [...SUPPORTED_ROLES];
    
    // Iterate over feature flagged roles and add them if the feature flag is enabled
    Object.keys(FEATURE_FLAGGED_ROLES).forEach((featureKey) => {
        if (features?.active?.[featureKey]) {
            enabledRoles = [...enabledRoles, ...FEATURE_FLAGGED_ROLES[featureKey]];
        }
    });
    
    return enabledRoles;
};

const READ_ONLY_STATE = {
    ELEVATED: "ELEVATED",
    DOWNSCOPED: "DOWNSCOPED"
}

const getUpdateRolesAndCategories = (roles = [], features = {}) => {
    const supportedRoles = getSupportedRoles(features);
    const categories = {}
    const updateRoles = roles
        .map((role) => {
            if (_.includes(supportedRoles, role.roleInternalName)) {
                const category =  _.find(role["tags"], tag => tag["internal:bxp:categoryType"]);
                role.category = isUndefined(category) ? category : category["internal:bxp:categoryType"];
            } else {
                role.category = undefined
            }
            
            if(role.category && !_.has(categories,  role.category)) {
                categories[role.category] = CATEGORY_MENU[role.category]
            }
            return role;
        });  
    
        return {
            categories,
            updateRoles
        }
}

const fetchOrSetStoredItem = ({ storageItemKey, collection, collectionItemKey, preSetItem }) => {
    const storedItemId = preSetItem || getItem(storageItemKey);

    let selectedItem = null;

    if (storedItemId) {
        selectedItem = _.find(collection, { [collectionItemKey]: storedItemId });
    }

    if (!selectedItem && collection?.length > 0) {
        if (storageItemKey?.itemKey === "userScopeId") {
            let index = _.findIndex(collection, (ele) => ele?.resourceType === 'project');
            selectedItem = collection[index > -1 ? index : 0];
        } else {
            selectedItem = collection[0];
        }
        setItem(storageItemKey, selectedItem[collectionItemKey]);
    } else if (!selectedItem) {
        removeItem(storageItemKey);
        return null;
    } else if (preSetItem) {
        setItem(storageItemKey, selectedItem[collectionItemKey]);
    }

    return selectedItem[collectionItemKey];
};

const gcpRegionRegex = /(.+)?\/(.+)?\/(.+)?\/(.+)?/;
const maxLimit = "limit=10000"

const prepareHeirarchy = (root, scopes) => {
    const childScopes = []
    _.forEach(scopes, (scope) => {
        if (root.id === scope.parentId) {
            childScopes.push(scope);
        }
    })
    root.childScopes = childScopes;
    _.forEach(root?.childScopes, (scope) => {
        prepareHeirarchy(scope, _.difference(scopes, childScopes));
    })
}

const fetchUsers = async (selectedOrgId, payload, isUserElevated, dispatch) => {
    try {
        const url = selectedOrgId ? `/organizations/${selectedOrgId}/users` : "/users"
        const { data: { items: users } } = await apiClient.get(`${externals.tenancyV4Backend}${url}?filter=auth0Id eq '${payload.userId}'`)
        dispatch({
            type: "TENANCYV4:FETCH-USER-SUCCESS",
            payload: {
                user: { ...users[0], isUserElevated} 
            }
        });
        dispatch({
            type: "TENANCYV4:FETCH-ROLES"
        });
        return Promise.resolve(users)
    } catch (e) {
        dispatch({
            type: "TENANCYV4:FETCH-USER-FAILED",
            payload: e
        })
        dispatch({
            type: "NOTIFICATIONS:ERROR",
            payload: {
                isFatal: true,
                message: t("failed_to_fetch_user_data",i18nNamespace),
                exception: e
            }
        })
        Promise.reject(e)
    }
            
}

const fetchOrgs = async (dispatch) => {
    try {
        const { data: {items: orgs} } = await apiClient.get(`${externals.tenancyV4Backend}/organizations?${maxLimit}`)
        return Promise.resolve(orgs)
    } catch (e) {
        dispatch({
            type: "TENANCYV4:FETCH-ORGS-FAILED",
            payload: e
        });
        Promise.reject(e)
    }
}

const fetchPartnerOrgs = async (dispatch, userId) => {
    try {
        const { data: { items: orgs } } = await apiClient.get(`${externals.tenancyV4Backend}/users/${userId}/partnerOrganizations?${maxLimit}`)
        return Promise.resolve(orgs)
    } catch (e) {
        dispatch({
            type: "TENANCYV4:FETCH-PARTNER-ORGS-FAILED",
            payload: e
        });
        Promise.reject(e)
    }
}

const fetchUserDetails = async (selectedOrgId, selectedScopeId, authUserId, dispatch) => {
    try {
        let url = `${externals.stargateV4Backend}/user-details`
        if(selectedOrgId) {
            if(selectedScopeId) {
                url = `${url}?organization_id=${selectedOrgId}&resource_type=project&resource_id=${selectedScopeId}`
            } else {
                url = `${url}?organization_id=${selectedOrgId}`
            }
        }
        if(isUndefined(selectedOrgId)) {
            const response = await apiClient.get(url)
            return Promise.resolve({response})
        } else {
            const response = await getCachedClient(apiClient, url, `user-list-${authUserId}`)
            return Promise.resolve(response);
        }
    } catch (e) {
        dispatch({
            type: "TENANCYV4:FETCH-ORGS-FAILED",
            payload: e
        });
        return Promise.reject(e);
    }
}

const fetchRoles = (userData, dispatch, features) => {
    const {updateRoles, categories} = getUpdateRolesAndCategories(userData.roles, features)
    dispatch({
        type: "TENANCYV4:FETCH-ROLES-SUCCESS",
        payload: {
            roles : updateRoles,
            categories
        }
    });
}

const sendGaAnalytics = ({ orgId, rules }) => {
    //retaining accountIdentity for backward compatibility
    const userProperties = { accountIdentity: orgId };
    if (!_.isEmpty(rules)) {
        userProperties["isOrgAdmin"] = isOrgAdmin(rules) ? "YES" : "NO";
    }
    setGAUserProperties(userProperties);
};

const sendFSAnalytics = ({ orgId, rules }) => {
    //retaining OrgId for backward compatibility
    const userProperties = { OrgId: orgId };
    if (!_.isEmpty(rules)) {
        userProperties["isOrgAdmin"] = isOrgAdmin(rules) ? "YES" : "NO";
    }
    setFullStoryUserValues(userProperties);
};

export const getTagValue = (tags, key) => {
    const tag = _.find(tags, tag => !_.isUndefined(tag[key]))
    return tag ? tag[key] : undefined;
}

const isUserElevated = async (orgId, userId) => {
    try {
        const { data } = await apiClient.get(`${externals.tenancyV4Backend}/resources/${orgId}/permissions/bxp:elevated-state/users/${userId}`)
        return !_.isUndefined(data) ? Promise.resolve(true) : Promise.resolve(false);
    } catch (e) {
        return Promise.resolve(false);
    }
}

const handleIsElevaledPrevilagesResponse = (response, {dispatch, userData, payload}) => {
    const { data } = response
    const orgs = [userData.organization]
    if (!_.isUndefined(orgs) && !_.isNull(orgs) && orgs.length > 0 && payload.userId) {
        dispatch({
            type: "TENANCYV4:FETCH-USER-SUCCESS",
            payload: {
                user: {auth0Id: userData.auth0Id, id: userData.id, name: userData.auth0Id, isUserElevated: !_.isUndefined(data)}
            }
        });
    }
}

const handleIsElevaledPrevilagesError = (response, {dispatch, userData, isOrgInSafeMode}) => {
    const { data } = response
    dispatch({
        type: "TENANCYV4:FETCH-USER-SUCCESS",
        payload: {
            user: {auth0Id: userData.auth0Id, id: userData.id, name: userData.auth0Id, isUserElevated: !isOrgInSafeMode ? undefined : false}
        }
    });
}



const handleUserDetailsResponse = async (response, { dispatch, state, tenancy, isOrgInSafeModeCached , payload, isNetworkResponse }) => {
            const { data: userData } = response;
            let orgs = [userData.organization];
            const isSetupMode = state.app.appState === "setup-wizard";
            let selectedOrgId = userData?.organization?.id;
            let selectedScopeId = userData?.resource?.id;
            let forbidden = true;

            dispatch({
                type: "TENANCYV4:UPDATE-SELECTED-ORG",
                payload: {
                    selectedOrg: orgs[0]
                }
            });

            if (!_.isUndefined(orgs) && !_.isNull(orgs)) {
                setItem({itemKey: "userOrgId", userId: state.auth.userId}, selectedOrgId);
                setItem({itemKey: "userScopeId", userId: state.auth.userId}, selectedScopeId);

                forbidden = tenancy.localAccountId ? (userData?.organization.legacyId !== tenancy.localAccountId && userData?.organization.id !== tenancy.localAccountId): false;

                if (orgs.length > 0 && userData.id) {

                    // Fetch-Users
                    dispatch({
                        type: "TENANCYV4:FETCH-USER-SUCCESS",
                        payload: {
                            user: {auth0Id: userData.auth0Id, id: userData.id, name: userData.auth0Id}
                        }
                    });
                }

                trackTiming({ category: "APP", name: "loaded orgs" });

                // TODO to be removed later
                orgs = orgs.map((org) => {
                    const legacyPublicTag = org.tags.find((tag) => !_.isUndefined(tag["internal:bxp:legacyId"]))
                    org.legacyId = legacyPublicTag ? legacyPublicTag["internal:bxp:legacyId"] : undefined;
                    return org
                })
            }
            
            dispatch({
                type: "TENANCYV4:FETCH-ORGS-SUCCESS",
                payload: {
                    selectedOrgId,
                    selectedScopeId,
                    forbidden,
                    orgsFullyLoaded : false,
                    orgs: _.isUndefined(orgs) || _.isNull(orgs) ? [] : orgs
                }
            });

            if (selectedOrgId && !isSetupMode) {
                enableGAPageViewTracking();
                sendGaAnalytics({ orgId: selectedOrgId, rules: orgs[0]?.rules });
                sendFSAnalytics({ orgId: selectedOrgId, rules: orgs[0]?.rules });

                // Fetch-Scopes
                dispatch({
                    type: "TENANCYV4:FETCH-SCOPES-LOADING"
                });

                let scopes = [userData.resource];

                dispatch({
                    type: "TENANCYV4:UPDATE-SELECTED-ORG",
                    payload: {
                        selectedOrg: orgs[0]
                    }
                });

                dispatch({
                    type: "TENANCYV4:FETCH-SCOPES-SUCCESS",
                    payload: {
                        scopes,
                        selectedScopeId,
                        scopesFullyLoaded: false
                    }
                })
                dispatch({
                    type: "TENANCYV4:SET-PERMISSIONS",
                    payload: {
                        scopeId: selectedScopeId
                    }
                })
            } else {
                dispatch({
                    type: "TENANCY:NO-AGENTS",
                });
            }
            const isOrgInSafeMode = getTagValue(userData?.organization?.tags,"internal:bxp:safeMode") === 'true' ? true : false;
            if(state.features?.active?.["NetApp.TenancyV4/safemode"] && isOrgInSafeMode !== !isOrgInSafeModeCached && userData  && isNetworkResponse) {
                if (isOrgInSafeMode) {
                    const {id: userId, organization: {id: orgId}} = userData;
                    try {
                        const response = await apiClient.get(`${externals.tenancyV4Backend}/resources/${orgId}/permissions/bxp:elevated-state/users/${userId}`);
                        handleIsElevaledPrevilagesResponse(response, { dispatch, userData, payload })
                    } catch (e) {
                        handleIsElevaledPrevilagesError(e, { dispatch, userData, isOrgInSafeMode })
                    }
                } else {
                    dispatch({
                        type: "TENANCYV4:FETCH-USER-SUCCESS",
                        payload: {
                            user: {auth0Id: userData.auth0Id, id: userData.id, name: userData.auth0Id, isUserElevated: false}
                        }
                    });
                }
            }
}

const handleListConnectorResponse = (response, { dispatch, state, payload, isTenancyV4 }) => {
        const { preSetConnectorId, processedV4Agents } = payload;
        const selectedAccountId = state.tenancy.selectedAccountId;
        const localStorageItemKey = { itemKey: "userConnectorId", userId: state.auth.userId };
        const firstTime = state.tenancy.agentStatus === "PENDING";
        const selectedScopeId = state.tenancy.selectedScopeId;
        const scopes = state.tenancy.scopes;
        let processedScopes = _.cloneDeep(scopes);
            const { data: { occms: agents } } = response
            let processedAgents = agents.map(occm => {
                const { agent, primaryCallbackUri } = occm;

                let region = _.get(agent, "region", "unknown");
                let provider = _.get(agent, "provider", "").toLowerCase();
                let status = _.get(agent, "status", "inactive");

                if (occm.status === "failed") {
                    status = "failed";
                }

                const name = _.get(agent, "name");
                const agentId = _.get(agent, "agentId");

                if (agent.provider === "gcp") {
                    const regionMatch = region.match(gcpRegionRegex);
                    if (regionMatch && regionMatch.length === 5) {
                        region = regionMatch[4];
                    }
                }

                if (provider === "onprem") {
                    provider = "on-prem";
                }

                return { ...occm, primaryCallbackUri, region, status, provider, name, agentId, id: agentId }
            });

            const activeAgents = _.filter(processedAgents, { status: "active" });
            processedAgents = _.sortBy(processedAgents, [(agent) => agent.status === 'active' ? -1 : agent.status === 'pending' ? 0 : 1], [agent => agent.name.toLowerCase()], 'name');
            // connectorChanges: Remove this comment once the agent association is approved. 
            // const collection = isTenancyV4 ? _.get(processedV4Agents, selectedScopeId, []) : (process.env.REACT_APP_IS_SAAS === "true" ? activeAgents : processedAgents); //We can allow connecting to offline connector in local mode
            const collection = process.env.REACT_APP_IS_SAAS === "true" ? activeAgents : processedAgents; //We can allow connecting to offline connector in local mode

            let preSetItem = preSetConnectorId;
            if (!firstTime) {
                preSetItem = state.tenancy.selectedAgentId;
            }
            // connectorChanges: Remove this comment once the agent association is approved. 
            // const selectedAgentId = fetchOrSetStoredItem({ storageItemKey: localStorageItemKey, collection, collectionItemKey: isTenancyV4 ? "auth0Id" : "agentId", preSetItem });
            const selectedAgentId = fetchOrSetStoredItem({ storageItemKey: localStorageItemKey, collection, collectionItemKey: "agentId", preSetItem });
            let isChangedAgent = false;
            let isTransitionFromUnselectedToSelected = false;
            if (!firstTime) {
                if (state.tenancy.selectedAgentId) {
                    isChangedAgent = state.tenancy.selectedAgentId !== selectedAgentId;
                } else {
                    isTransitionFromUnselectedToSelected = !!selectedAgentId;
                }
            } else {
                trackTiming({ category: "APP", name: "loaded agents" })
            }

            if (process.env.REACT_APP_IS_SAAS === "true") {
                setOccmAgent({ agentId: selectedAgentId, accountId: selectedAccountId });
            }

            setForwarderAgent({ agentId: selectedAgentId, accountId: selectedAccountId });

            if (isTenancyV4 && processedV4Agents) {
                // Recursive function to associate agents object inside each scope object which matches the scopeId with v4 agents projectId/FolderId/OrganizationId and list-connectors agents ID with auth0Id of v4 agents
                const recursiveProcessScope = (scopeValues) => {
                    return scopeValues.map((scope) => {
                        if (!_.isUndefined(scope.childScopes) && scope.childScopes.length > 0) {
                            scope.childScopes = recursiveProcessScope(scope.childScopes)
                        }
                        if (processedV4Agents[scope.id]) {
                            const scopeAgents = processedV4Agents[scope.id].map((agentsV4) => {
                                const index = _.findIndex(processedAgents, (agent) => agent?.agent?.agentId === agentsV4.auth0Id)
                                if (index > -1) {
                                    return processedAgents[index]
                                }
                            })
                            return {
                                ...scope,
                                associatedAgents: scopeAgents
                            }
                        }
                        return {
                            ...scope,
                            associatedAgents: []
                        }
                    })
                }

                if (scopes) {
                    processedScopes = recursiveProcessScope(scopes);
                }

                dispatch({
                    type: "TENANCYV4:LOAD-AGENTS-SUCCESS",
                    payload: {
                        scopes: processedScopes,
                        selectedScopeId
                    }
                });
            }

            dispatch({
                type: "TENANCY:LOAD-AGENTS-SUCCESS",
                payload: {
                    agents: processedAgents,
                    selectedAgentId,
                    isChangedAgent,
                    isTransitionFromUnselectedToSelected
                }
            });
            if (isTenancyV4 && process.env.REACT_APP_IS_NCL !== "true") {
                const agentUserId = getItem({ itemKey: `userConnectorIdKey.${selectedAgentId}` });
                dispatch({
                    type: "TENANCY:SET-AGENT-USER",
                    payload: {
                        agentId: selectedAgentId,
                        agentUserId: process.env.REACT_APP_IS_NCL === "true" ? selectedAgentId : agentUserId
                    }
                })
            } else {
                dispatch({
                    type: "TENANCY:SET-AGENT-USER-SUCCESS",
                    payload: { }
                })
            }
            if (isChangedAgent && !selectedAgentId) {
                //Bug in angular, can't switch from having agent to not having agent, so refresh Iframe
                dispatch({
                    type: "APP:NG-DE-INIT"
                });
                setTimeout(() => {
                    dispatch({
                        type: "APP:NG-RE-INIT"
                    });
                }, 100);
            }

            if (isTransitionFromUnselectedToSelected) {
                setTimeout(() => {
                    dispatch({
                        type: "TENANCY:DISCARD-CHANGE-AGENT-MESSAGE"
                    })
                }, 8000)
            }
}

export default ({ dispatch, getState }) => next => async (action) => {
    const { type, payload } = action;
    const isTenancyV4 = isNewTenancyEnabled();
    let userData = null;
    
    if (type === "TENANCYV4:CREATE-ORGANIZATION") {
        const { organizationName, resourceType, resourceClass, version, type } = payload;

        return handleApiAction(action, () => {
            return apiClient.post(`${externals.tenancyV4Backend}/organizations`, {
                name: organizationName,
                resourceType,
                resourceClass,
                version,
                type,
            });
        }).then(() => {
            window.location.reload();
        }).catch(e => {
            const errMessage = _.get(e, 'response.data.invalidParams[0].reason') ?? "Unknown";
            dispatch({
                type: "TENANCY:CREATE-ORGANIZATION-ERROR",
                payload: {
                    errMessage
                }
            })
        })

    } else if (type === "TENANCYV4:FETCH-ROLES") {
        try {
            const state = getState();
            const { data: { items: roles } } = await apiClient.get(`${externals.tenancyV4Backend}/roles`);
            const {updateRoles, categories} = getUpdateRolesAndCategories(roles, state.features);
            
            dispatch({
                type: "TENANCYV4:FETCH-ROLES-SUCCESS",
                payload: {
                    roles : updateRoles,
                    categories
                }
            });
        } catch (e) {
            dispatch({
                type: "TENANCYV4:FETCH-ROLES-FAILED",
                payload: e
            });
        }
    } else if (type === "TENANCYV4:FETCH-ORGS") {
        const state = getState();
        const tenancy = state.tenancy;
        const { pollAgents, avoidLoadScopes } = payload;

        if (tenancy.orgStatus !== "PENDING" && tenancy.orgsFullyLoaded) {
            return;
        }

        const search = querystring.parse(history.location.search);
        let preSetOrgId = search.orgId;
        const preSetScopeId = search.scopeId;

        if (search.orgId || search.scopeId || search.connectorId) {
            const searchString = querystring.stringify(_.omit(search,['orgId', 'scopeId', 'connectorId']));
            history.replace(history.location.pathname + (searchString ? `?${searchString}` : ''));           
        }

        try {
            const localStorageItemKey = { itemKey: "userOrgId", userId: state.auth.userId };
            trackTiming({ category: "APP", name: "started orgs" });
            let orgs = await fetchOrgs(dispatch)
            let partnerOrgresponse;
            if (process.env.REACT_APP_IS_SAAS === "true" && process.env.REACT_APP_IS_DARK !== "true") {
                partnerOrgresponse = await fetchPartnerOrgs(dispatch, state.auth.userId);
            }
            const isSetupMode = state.app.appState === "setup-wizard";
            let selectedOrgId = null;
            let forbidden = true;
            const partnerOrgIdList = [];
            let partnerOrgs = []

            if (!_.isUndefined(partnerOrgresponse) && !_.isNull(partnerOrgresponse)) {
                partnerOrgs = partnerOrgresponse.map((org) => {
                    partnerOrgIdList.push(org.id);
                    const legacyPublicTag = org.tags?.find((tag) => !_.isUndefined(tag["internal:bxp:legacyId"]))
                    org.legacyId = legacyPublicTag ? legacyPublicTag["internal:bxp:legacyId"] : undefined;
                    org['isPartnerOrg'] = true;
                    return org
                })
            }

            if (!_.isUndefined(orgs) && !_.isNull(orgs)) {
                if (tenancy.localAccountId) {
                    preSetOrgId = orgs.find((org) => (org?.legacyId === tenancy.localAccountId || org?.id === tenancy.localAccountId))?.id;
                }
                selectedOrgId = fetchOrSetStoredItem({ storageItemKey: localStorageItemKey, collection: orgs, collectionItemKey: "id", preSetItem: preSetOrgId });
                forbidden = tenancy.localAccountId ? preSetOrgId !== selectedOrgId: false;
                if (forbidden) {
                    selectedOrgId = null;
                }
                // Load users details only where there is org associated with it.
                if (orgs.length > 0 && payload.userId) {
                    const selectedOrg = _.find(orgs, (org) => org.id === selectedOrgId);
                    const isOrgInSafeMode = selectedOrg && getTagValue(selectedOrg.tags,"internal:bxp:safeMode") === 'true' ? true : false;
                    let userElevated = false;
                    if(state.features?.active?.["NetApp.TenancyV4/safemode"] && isOrgInSafeMode) {
                        userElevated = await isUserElevated(selectedOrgId, payload.userId);
                    }
                    await fetchUsers(selectedOrgId, payload, userElevated, dispatch)
                }
    
                trackTiming({ category: "APP", name: "loaded orgs" });

                // TODO to be removed later
                orgs = orgs.map((org) => {
                    const legacyPublicTag = org.tags.find((tag) => !_.isUndefined(tag["internal:bxp:legacyId"]))
                    org.legacyId = legacyPublicTag ? legacyPublicTag["internal:bxp:legacyId"] : undefined;
                    return org
                }).filter((org) => partnerOrgIdList.length > 0 ? !partnerOrgIdList.includes(org.id) : org)
            }
            

            dispatch({
                type: "TENANCYV4:FETCH-ORGS-SUCCESS",
                payload: {
                    selectedOrgId,
                    forbidden,
                    orgsFullyLoaded: true,
                    orgs: _.isUndefined(orgs) || _.isNull(orgs) ? [] : orgs,
                    partnerOrgs: _.isUndefined(partnerOrgs) || _.isNull(partnerOrgs) ? [] : partnerOrgs
                }
            });

            if (selectedOrgId && !isSetupMode && !avoidLoadScopes) {
                enableGAPageViewTracking();
                sendGaAnalytics({ orgId: selectedOrgId });
                sendFSAnalytics({ orgId: selectedOrgId });

                dispatch({
                    type: "TENANCYV4:FETCH-SCOPES",
                    payload: {
                        preScopeId: preSetScopeId,
                        pollAgents
                    }
                });
            } else if (getState().tenancy.agentStatus !== "SUCCESS") {
                dispatch({
                    type: "TENANCY:NO-AGENTS",
                });
            }
        } catch (e) {
            dispatch({
                type: "NOTIFICATIONS:ERROR",
                payload: {
                    isFatal: true,
                    message: t("failed_to_load_org_structure",i18nNamespace),
                    exception: e
                }
            })
        }
    } else if (type === "TENANCYV4:FETCH-USER-DETAILS") {
        const state = getState();
        const tenancy = state.tenancy;
        const { pollAgents } = payload;

        if (tenancy.orgStatus !== "PENDING") {
            return;
        }

        const search = querystring.parse(history.location.search);
        let preSetOrgId = search.orgId;
        let preSetScopeId = search.scopeId;

        if (search.orgId || search.scopeId || search.connectorId) {
            const searchString = querystring.stringify(_.omit(search,['orgId', 'scopeId', 'connectorId']));
            history.replace(history.location.pathname + (searchString ? `?${searchString}` : ''));            
        }

        try {
            const authUserId = state.auth.userId;
            const localStorageItemKey = { itemKey: "userOrgId", userId: state.auth.userId };
            trackTiming({ category: "APP", name: "started orgs" });
            let storedOrgId = getItem(localStorageItemKey)
            preSetOrgId = preSetOrgId ? preSetOrgId : storedOrgId;

            const localStorageScopeKey = { itemKey: "userScopeId", userId: state.auth.userId };
            const storedScopeId = getItem(localStorageScopeKey);
            preSetScopeId = preSetScopeId ? preSetScopeId : storedScopeId;

            const { response, networkResponse} = await fetchUserDetails(preSetOrgId, preSetScopeId, authUserId, dispatch);
            const { data: userData } = response;
            const org = _.cloneDeep(userData?.organization);
            let orgs = [org];
            const isOrgInSafeMode = getTagValue(org.tags,"internal:bxp:safeMode") === 'true' ? true : false;
            handleUserDetailsResponse(response, { dispatch, state, payload, tenancy, isOrgInSafeModeCached: undefined, isNetworkResponse: false});
            handleNetworkResponse(networkResponse, handleUserDetailsResponse, (error)=> {
                dispatch({
                    type: "TENANCYV4:FETCH-ORGS",
                    payload: { pollAgents: true }
                });
            }, { dispatch, state, payload, tenancy, isOrgInSafeModeCached: isOrgInSafeMode, isNetworkResponse: true });

            if(state.features?.active?.["NetApp.TenancyV4/safemode"] && isOrgInSafeMode && userData) {
                const {id: userId, organization: {id: orgId}} = userData;
                try {
                const response = await apiClient.get(`${externals.tenancyV4Backend}/resources/${orgId}/permissions/bxp:elevated-state/users/${userId}`);
                handleIsElevaledPrevilagesResponse(response, { dispatch, userData, payload })
                } catch (e) {
                    handleIsElevaledPrevilagesError(e, { dispatch, userData, isOrgInSafeMode })
                }
            }

            // Load users details only where there is org associated with it.
            if (!_.isUndefined(orgs) && !_.isNull(orgs) && orgs.length > 0 && payload.userId) {
                // Fetch-Roles
                fetchRoles(userData, dispatch, state.features);
            }

            const isSetupMode = state.app.appState === "setup-wizard";
            let selectedOrgId = userData?.organization?.id;
            if (selectedOrgId && !isSetupMode) {
                const search = querystring.parse(history.location.search);
                const preSetConnectorId = state.tenancy.localConnectorId || search.connectorId;

                dispatch({
                    type: "TENANCY:LOAD-AGENTS",
                    payload: {
                        preSetConnectorId,
                        first: true,
                        noPoll: !pollAgents
                    }
                });
            }

            // Verify org switch if target org ID exists in local storage
            const targetOrgId = getItem({ itemKey: "targetOrgId", userId: state.auth.userId });
            if (targetOrgId) {
                removeItem({ itemKey: "targetOrgId", userId: state.auth.userId });
                if (targetOrgId !== selectedOrgId) {
                    dispatch({
                        type: "NOTIFICATIONS:ERROR",
                        payload: {
                            message: "Failed to switch organization."
                        }
                    });
                }
            }
            
            // To handle a case, when user directly comes to IAM page from the URL
            const isIAMPageOpen = window.location.pathname.includes("/identity-access-mgmt");
            if(isIAMPageOpen) {
                dispatch({
                    type: "TENANCYV4:FETCH-SCOPES",
                    payload: {
                        preScopeId: preSetScopeId,
                        pollAgents
                    }
                });
            }

        } catch (e) {
            //  Fetch orgs if the user details API fails
            dispatch({
                    type: "TENANCYV4:FETCH-ORGS",
                    payload: { pollAgents: true }
            });
        }
    } else if (type === "TENANCYV4:SET-ORG") {
        const { orgId } = payload;
        const state = getState();
        const userId = state.auth.userId;

        // Store target org ID in local storage to verify successful switch after reload
        if (orgId) {
            setItem({ itemKey: "targetOrgId", userId }, orgId);
        }

        setItem({ itemKey: "userOrgId", userId }, orgId);
        const selectedOrg = _.find([...state.tenancy.orgs, ...state.tenancy?.partnerOrgs], { id: orgId })
        setItem({ itemKey: "userAccountId", userId }, selectedOrg.legacyId);
        removeItem({ itemKey: "userScopeId", userId });

        if (window.location.pathname.startsWith("/storage")) {
            window.location.replace("/");
        } else {
            window.location.reload();
        }
    } else if (type === "TENANCYV4:FETCH-SCOPES") {
        const { preScopeId, pollAgents, forceReload } = payload;
        const state = getState();

        if(state.tenancy.scopesFullyLoaded && !forceReload) {
            prepareHeirarchy(state.tenancy.selectedOrg, state.tenancy.scopes);
            return;
        }

        const selectedOrgId = state.tenancy.selectedOrgId;
        const userId = state.tenancy.user?.id;
        const localStorageItemKey = { itemKey: "userScopeId", userId: state.auth.userId };
        const search = querystring.parse(history.location.search);
        const preSetConnectorId = state.tenancy.localConnectorId || search.connectorId;
        

        dispatch({
            type: "TENANCYV4:FETCH-SCOPES-LOADING"
        });

        try {
            const apiCalls = [
                apiClient.get(`${externals.tenancyV4Backend}/organizations/${selectedOrgId}/resources?filter=resourceClass eq 'hierarchy' and resourceType ne 'organization'&${maxLimit}`),
                apiClient.get(`${externals.tenancyV4Backend}/permissions?filter=service_application eq 'bxp'&${maxLimit}`),
                apiClient.get(`${externals.tenancyV4Backend}/ui/organizations/${selectedOrgId}/users/${userId}/uiroles?${maxLimit}`),
            ]

            let [{ data: { items: scopes } }, { data: { items: permissions } }, { data: { items: scopeRoles } }] = await Promise.all(apiCalls);
            const selectedScopeId = scopes ? fetchOrSetStoredItem({ storageItemKey: localStorageItemKey, collection: scopes, collectionItemKey: "id", preSetItem: preScopeId }) : undefined;
            const selectedOrg = _.find([...state.tenancy.orgs, ...state.tenancy?.partnerOrgs], { id: selectedOrgId })
            let permissionMap = {};
            for (const scope of scopeRoles) {
                const roleMap = {}
                for (const r of scope.roles) {
                    roleMap[r.id] = r;
                }

                permissionMap[scope.id] = permissions?.filter(p => p.tags
                    ?.filter(tag => Object.keys(tag)[0] === 'internal:bxp:roleId')
                    ?.find(tag => scope?.roles?.length > 0 && !isUndefined(roleMap[tag['internal:bxp:roleId']])))

                if (permissionMap[scope.id].length === 0) {
                    permissionMap[scope.id].push(permissions?.find(p => p.rule === `bxp:${scope.resourceType}-read`))
                }
            }

            // TODO to be removed when the legacyId is a resource variable
            scopes = scopes ? scopes.map((scope) => {
                if (scope.resourceType === "project") {
                    const legacyPublicTag = scope.tags.find((tag) => !_.isUndefined(tag["internal:bxp:legacyId"]))
                    if(!_.isUndefined(legacyPublicTag)) {
                        scope.legacyId = legacyPublicTag["internal:bxp:legacyId"];
                    }
                }
                const rules = permissionMap[scope.id]?.filter(permission => permission.rule.startsWith('bxp:'))
                scope['rules'] = isUndefined(rules) || rules.length === 0 ? [`bxp:${scope.resourceType}-read`] : rules.map(permission => permission.rule);
                const roles = scopeRoles?.filter(scopeRole => scope.id === scopeRole.id);
                scope['roles'] = isUndefined(rules) || roles.length === 0 ? [] : _.merge(...roles.map(scopeRole => scopeRole.roles))
                return scope
            }) : []
            const orgRules = permissionMap[selectedOrg.id]?.filter(permission => permission.rule.startsWith('bxp:'))
            selectedOrg['rules'] = isUndefined(orgRules) || orgRules.length === 0 ? ['bxp:organization-read'] : orgRules.map(permission => permission.rule)
            prepareHeirarchy(selectedOrg, scopes);

            sendFSAnalytics({ orgId: selectedOrg.id, rules: selectedOrg.rules });
            sendGaAnalytics({ orgId: selectedOrg.id, rules: selectedOrg.rules });

            dispatch({
                type: "TENANCYV4:UPDATE-SELECTED-ORG",
                payload: {
                    selectedOrg
                }
            });

            dispatch({
                type: "TENANCYV4:FETCH-SCOPES-SUCCESS",
                payload: {
                    scopes,
                    selectedScopeId,
                    scopesFullyLoaded: true
                }
            })
            dispatch({
                type: "TENANCYV4:SET-ROLES-PERMISSIONS",
                payload: {
                    permissions: permissions
                }
            })
            dispatch({
                type: "TENANCYV4:SET-PERMISSIONS",
                payload: {
                    scopeId: selectedScopeId
                }
            })
            dispatch({
                type: "TENANCY:LOAD-AGENTS",
                payload: {
                    preSetConnectorId,
                    first: true,
                    noPoll: !pollAgents
                }
            });
        } catch (e) {
            dispatch({
                type: "TENANCYV4:FETCH-SCOPES-FAILED",
                payload: e
            })

            dispatch({
                type: "NOTIFICATIONS:ERROR",
                payload: {
                    isFatal: true,
                    message: t("failed_to_load_folders_projects",i18nNamespace),
                    exception: e
                }
            })
        }
    } else if (type === "TENANCYV4:AIDE-PERMISSIONS") {
        const state = getState();
        const userId = state.auth.userId;
        const supportServices = state.app.supportServices;
        const tenancyUrl = _.get(supportServices, 'tenancyServiceInformation.url', process.env.REACT_APP_AIDE_TENANCY_API_URL);
        dispatch({
            type: "TENANCYV4:FETCH-ORGS-PENDING"
        });

        try {
            const { data: userObj } = await occmClient.get(`${tenancyUrl}/users/${userId}`)
            const ownerOrgId = userObj.ownerOrganizationId;

            const { data: { items: permissions } } = await occmClient.get(`${tenancyUrl}/users/${userId}/resources/${ownerOrgId}/permissions?filter=service_application eq 'bxp'&${maxLimit}`);
            const selectedOrg = { id: ownerOrgId }
            const scopes = [selectedOrg];
            selectedOrg['rules'] = permissions.map(permission => permission.rule)

            dispatch({
                type: "TENANCYV4:AIDE-PERMISSIONS-SUCCESS",
                payload: {
                    orgs: scopes,
                    selectedOrg,
                    selectedOrgId: selectedOrg.id,
                    scopes,
                    scopesFullyLoaded: true
                }
            })
        } catch (e) {
            if (e?.response?.status === 401) {
                dispatch({
                    type: 'AUTH:LOGOUT'
                });
            }
            dispatch({
                type: "TENANCYV4:FETCH-ORGS-FAILED",
                payload: e
            })

            dispatch({
                type: "NOTIFICATIONS:ERROR",
                payload: {
                    isFatal: true,
                    message: "Failed to load permissions",
                    exception: e
                }
            })
        }
    } else if (type === "TENANCYV4:SET-SCOPE") {
        const state = getState();
        const { scopeId } = payload;
        const userId = state.auth.userId;

        setItem({ itemKey: "userScopeId", userId }, scopeId);

        const selectedScope = _.find(state.tenancy.scopes, { id: scopeId });
        if (selectedScope?.associatedAgents) {
            const selectedAccountId = state.tenancy.selectedAccountId;
            const selectedAgentId = (selectedScope?.associatedAgents?.length > 0) ? selectedScope?.associatedAgents[0].agentId : undefined;

            if (process.env.REACT_APP_IS_SAAS === "true") {
                setOccmAgent({ agentId: selectedAgentId, accountId: selectedAccountId });
            }
            setForwarderAgent({ agentId: selectedAgentId, accountId: selectedAccountId });
        }
        dispatch({
            type: "TENANCYV4:SET-PERMISSIONS",
            payload: {
                scopeId
            }
        })
    } else if (type === "TENANCYV4:LOAD-ADMIN-ORGS") {
        // This is used to load organizations with admin access for the logged in users.
        // This is used by the associate subscription workflow in digital wallet.
        const state = getState();
        const adminOrgs = state.tenancy.adminOrgs;
        const userId = state.tenancy.user?.id;
        if (!adminOrgs) {
            try {
            const { data: { items: permissions } } = await apiClient.get(`${externals.tenancyV4Backend}/permissions?filter=rule eq 'bxp:organization-modify'`);
            const adminPermissionId = permissions.length > 0 ? permissions[0].id : undefined;
                if (adminPermissionId) {
                    const { data: { items: resources } } = await apiClient.get(`${externals.tenancyV4Backend}/users/${userId}/permissions/${adminPermissionId}/resources?filter=resourceType eq 'organization'&${maxLimit}`);
                    const adminOrgs = resources.filter((resource) => resource.resourceType === 'organization');
                    dispatch({
                        type: "TENANCYV4:LOAD-ADMIN-ORGS-SUCCESS",
                        payload: {
                            adminOrgs
                        }
                    });
                } else {
                    dispatch({
                        type: "TENANCYV4:LOAD-ADMIN-ORGS-FAILED"
                    })
                }
            } catch (e) {
                dispatch({
                    type: "TENANCYV4:LOAD-ADMIN-ORGS-FAILED",
                    payload: e
                })
    
                dispatch({
                    type: "NOTIFICATIONS:ERROR",
                    payload: {
                        isFatal: true,
                        message: t("failed_to_load_org_with_admin_cred",i18nNamespace),
                        exception: e
                    }
                })
            }

        }
    } else if (type === "TENANCYV4:LOAD-AGENTS") {
        const state = getState();
        const { preSetConnectorId, first, noPoll } = payload;
        const selectedOrgId = state.tenancy.selectedOrgId;

        const { data: { items: agentsV4 } } = await apiClient.get(`${externals.tenancyV4Backend}/organizations/${selectedOrgId}/users?filter=userType eq "agent"&${maxLimit}`);

        // Filter v4 agents which has ProjectId/FolderId tag
        const filteredV4Agents = _.filter(agentsV4, (agentV4) => {
            return (agentV4.tags.filter((tag) => tag["internal:bxp:projectId"] || tag["internal:bxp:folderId"] || tag["internal:bxp:organizationId"]))?.length > 0
        });

        // Group V4 agents by projectId, FolderId and OrganizationId as key
        const processedV4Agents = _.groupBy(filteredV4Agents, (agentV4) => {
            const projectId = _.get(_.find(agentV4.tags, (tag) => tag["internal:bxp:projectId"]), "internal:bxp:projectId");
            const folderId =  _.get(_.find(agentV4.tags, (tag) => tag["internal:bxp:folderId"]), "internal:bxp:folderId");
            const organizationId =  _.get(_.find(agentV4.tags, (tag) => tag["internal:bxp:organizationId"]), "internal:bxp:organizationId");
            return projectId ?? folderId ?? organizationId
        });

        dispatch({
            type: "TENANCY:LOAD-AGENTS",
            payload: {
                preSetConnectorId,
                first,
                noPoll,
                processedV4Agents
            }
        });
        
    } else if (type === "TENANCY:LOAD-ACCOUNTS") {
        // TODO to be cleaned up
        const tenancy = getState().tenancy;

        if (tenancy.accountStatus !== "PENDING") {
            return;
        }

        const search = querystring.parse(history.location.search);
        const preSetAccountId = tenancy.localAccountId || search.accountId;
        const preWorkspaceId = search.workspaceId;
        const preSetConnectorId = tenancy.localConnectorId || search.connectorId;

        if (search.accountId || search.workspaceId || search.connectorId) {
            delete search.accountId;
            delete search.workspaceId;
            delete search.connectorId;
            const searchString = querystring.stringify(search);

            history.replace(history.location.pathname + (searchString ? `?${searchString}` : ''));
        }

        try {
            const state = getState();
            const localStorageItemKey = { itemKey: "userAccountId", userId: state.auth.userId };
            trackTiming({ category: "APP", name: "started accounts" });
            const { data: accounts } = payload?.accounts ? { data: payload.accounts } : await apiClient.get(`${externals.tenancyBackend}/account`);
            const setupMode = state.app.appState === "setup-wizard";

            let selectedAccountId = fetchOrSetStoredItem({ storageItemKey: localStorageItemKey, collection: accounts, collectionItemKey: "accountPublicId", preSetItem: preSetAccountId });
            const forbidden = tenancy.localAccountId ? preSetAccountId !== selectedAccountId : false;

            if (forbidden) {
                selectedAccountId = null;
            }

            trackTiming({ category: "APP", name: "loaded accounts" });

            if(process.env.REACT_APP_NETAPP_ACCOUNT_AUTO_JOIN === "true" && state.auth.isNetAppInternal && accounts.length === 0 && matchPath(window.location.pathname,'/app-redirect/active-iq') && !sessionStorage.getItem("occm.activeiq.reload")) {
                try {
                    await apiClient.post(`${externals.portalBackend}/portal/occm-demo-signup`, null, {
                        params: {
                            autoJoin: true
                        }
                    })

                    sessionStorage.setItem("occm.activeiq.reload", "true")
                    await dispatch({
                        type: "APP/RELOAD"
                    })
                } catch (e) {
                    console.log(e);
                }
            }

            if(accounts.length === 0 && !sessionStorage.getItem("occm.activeiq.reload")) {
                try {
                    const { data : { invitationCount, hasJoinedNewAccounts } } = await apiClient.post(`${externals.tenancyBackend}/users/apply-invitation`)

                    if(invitationCount > 0 && hasJoinedNewAccounts) {
                        sessionStorage.setItem("occm.activeiq.reload", "true")
                        await dispatch({
                            type: "APP/RELOAD"
                        })
                    }
                } catch (e) {
                    console.log(e);
                }
            }

            dispatch({
                type: "TENANCY:LOAD-ACCOUNTS-SUCCESS",
                payload: {
                    selectedAccountId,
                    forbidden,
                    accounts
                }
            });

            if (selectedAccountId && !setupMode) {
                dispatch({
                    type: "TENANCY:LOAD-AGENTS",
                    payload: {
                        preSetConnectorId,
                        first: true
                    }
                });

                dispatch({
                    type: "TENANCY:LOAD-WORKSPACES",
                    payload: {
                        preWorkspaceId
                    }
                });
            } else {
                dispatch({
                    type: "TENANCY:NO-AGENTS",
                });
            }
        } catch (e) {
            dispatch({
                type: "TENANCY/LOAD-FAILED",
                payload: e
            })

            dispatch({
                type: "NOTIFICATIONS:ERROR",
                payload: {
                    isFatal: true,
                    message:t("failed_to_load_accounts",i18nNamespace),
                    exception: e
                }
            })
        }
        // end
    } else if (type === "TENANCY:LOAD-AGENTS") {
        const state = getState();
        const { preSetConnectorId, noPoll } = payload;
        const selectedAccountId = state.tenancy.selectedAccountId;
        const firstTime = state.tenancy.agentStatus === "PENDING";

        const darkSiteAgent = () => {
            return {"response": {
                "data": {
                    "occms": [{
                        "account": state.app.about.siteIdentifier.company,
                        "accountName": state.app.about.siteIdentifier.company,
                        "occm": state.app.about.systemId,
                        "agentId": state.app.about.systemId,
                        "status": "ready",
                        "occmName": state.app.about.siteIdentifier.site,
                        "agent": {
                            "name": state.app.about.siteIdentifier.site,
                            "agentId": `${process.env.REACT_APP_IS_NCL === "true" ? state.app.about.systemId : state.app.supportServices.portalService.auth0Information.clientId+"clients"}`,
                            "provider": process.env.REACT_APP_IS_NCL === "true" ? "ONPREM" :state.app.instancePlacement.installLocation,
                            "status": "active",
                            "region": process.env.REACT_APP_IS_NCL === "true" ? "ONPREM" : state.app.instancePlacement.region,
                            "usingDockerInfra": process.env.REACT_APP_IS_NCL !== "true",
                            "clientInfraStatus": {
                                cloudmanager_cbs: {
                                    version: "cloudmanager_cbs:ds",
                                    reason: "",
                                    status: "active"
                                },
                                cloudmanager_compliance: {
                                    version: "cloudmanager_compliance:ds",
                                    reason: "",
                                    status: "active"
                                },
                                "cloudmanager_ontap-proxy": {
                                    version: "cloudmanager_ontap-proxy:ds",
                                    reason: "",
                                    status: "active"
                                }
                            }
                        }
                    }]
                }}
            }
        };
        try {
            const { response, networkResponse } = process.env.REACT_APP_IS_DARK !== "true" ? await getCachedClient(apiClient, `${externals.agentsBackend}/list-connectors/${selectedAccountId}`, `connectors-list-${selectedAccountId}`, {}, !firstTime) : darkSiteAgent()
            handleListConnectorResponse(response, { dispatch, state, payload, isTenancyV4});
            handleNetworkResponse(networkResponse, handleListConnectorResponse, (error) => {
                console.error("Failed to load agents", error);
            }, { dispatch, state, payload, isTenancyV4 });
        } catch (e) {
            if (firstTime) {
                dispatch({
                    type: "TENANCY:LOAD-AGENTS-FAILED",
                    payload: e
                });
                dispatch({
                    type: "NOTIFICATIONS:ERROR",
                    payload: {
                        isFatal: true,
                        message: t("failed_to_load_agent_details",i18nNamespace),
                        exception: e
                    }
                });
            }
        } finally {
            if (!noPoll) {
                setTimeout(() => {
                    dispatch({
                        type: "TENANCY:LOAD-AGENTS",
                        payload: {
                            preSetConnectorId: null
                        }
                    })
                }, 30 * 1000);
            }
        }
    } else if (type === "TENANCY:SET-AGENT") {
        const { agentId } = payload;
        const state = getState();
        const userId = state.auth.userId;
        const accountId = state.tenancy.selectedAccountId;
    
        if (process.env.REACT_APP_IS_SAAS === "true") {
            setOccmAgent({ agentId, accountId });
        }
    
        setForwarderAgent({ agentId, accountId });
    
        setItem({ itemKey: "userConnectorId", userId }, agentId);
        if (isTenancyV4) {
            // Resolve agentUserId from localStorage before the reducer runs
            // so selectedAgentId and selectedAgentUserId update atomically
            const resolvedAgentUserId = state.tenancy.userAgentMap?.[agentId]
                || getItem({ itemKey: `userConnectorIdKey.${agentId}` });

            // Pass enriched action to reducer, then dispatch SET-AGENT-USER for any remaining resolution
            next({ ...action, payload: { ...payload, agentUserId: resolvedAgentUserId } });

            dispatch({
                type: "TENANCY:SET-AGENT-USER",
                payload: { agentId, agentUserId: resolvedAgentUserId }
            });
            return;
        }
    } else if (type === "TENANCY:SET-AGENT-USER") {
        const { agentId, agentUserId } = payload;
        const state = getState();
        const userAgentMap = state.tenancy.userAgentMap;
        const selectedOrgId = state.tenancy.selectedOrgId;
        if (!agentId) {
            dispatch({
                type: "TENANCY:SET-AGENT-USER-SUCCESS",
                payload: { agentUserId,  agentId}
            })
        } else if (agentUserId) {
            dispatch({
                type: "TENANCY:SET-AGENT-USER-SUCCESS",
                payload: { agentUserId, agentId }
            })
        } else if (userAgentMap && userAgentMap[agentId]) {
            dispatch({
                type: "TENANCY:SET-AGENT-USER-SUCCESS",
                payload: { agentUserId: userAgentMap[agentId] }
            })
            setItem({ itemKey: `userConnectorIdKey.${agentId}` }, userAgentMap[agentId]);
        } else {
            try {
                const { response: { data: { items: users } } } = await getCachedClient(apiClient, `${externals.tenancyV4Backend}/organizations/${selectedOrgId}/users?filter=auth0Id eq '${agentId}' and userType eq 'agent'`, `agent-user-${agentId}`, undefined, false, "cache-first");
                dispatch({
                    type: "TENANCY:SET-AGENT-USER-SUCCESS",
                    payload: { agentId, agentUserId: users[0].id }
                })
                setItem({ itemKey: `userConnectorIdKey.${agentId}` }, users[0].id);
            }  catch (e) {
                dispatch({
                    type: "TENANCY:SET-AGENT-USER-FAILED",
                    payload: e
                })
    
                dispatch({
                    type: "NOTIFICATIONS:ERROR",
                    payload: {
                        isFatal: true,
                        message: t("failed_to_load_agent_details",i18nNamespace),
                        exception: e
                    }
                })
            }
        }
    }
    // TODO to be removed
    else if (type === "TENANCY:LOAD-WORKSPACES") {
        
        const state = getState();
        const { preWorkspaceId } = payload;
        const selectedAccountId = state.tenancy.selectedAccountId;
        const localStorageItemKey = { itemKey: "userWorkspaceId", userId: state.auth.userId };

        try {
            const { data: workspaces } = await apiClient.get(`${externals.tenancyBackend}/account/${selectedAccountId}/workspace`)

            const selectedWorkspaceId = fetchOrSetStoredItem({ storageItemKey: localStorageItemKey, collection: workspaces, collectionItemKey: "workspacePublicId", preSetItem: preWorkspaceId });

            dispatch({
                type: "TENANCY:LOAD-WORKSPACES-SUCCESS",
                payload: {
                    workspaces,
                    selectedWorkspaceId
                }
            })
        } catch (e) {
            dispatch({
                type: "TENANCY:LOAD-WORKSPACES-FAILED",
                payload: e
            })

            dispatch({
                type: "NOTIFICATIONS:ERROR",
                payload: {
                    isFatal: true,
                    message: t("failed_to_load_workspaces",i18nNamespace),
                    exception: e
                }
            })
        }
    } else if (type === "TENANCY:SET-ACCOUNT") {
        const { accountId } = payload;
        const userId = getState().auth.userId;

        setItem({ itemKey: "userAccountId", userId }, accountId);
        if (window.location.pathname.startsWith("/storage")) {
            window.location.replace("/");
        } else {
            window.location.reload();
        }
    } else if (type === "TENANCY/SET-PROJECT") {
        const { projectId, organizationId } = payload;
        const userId = getState().auth.userId;

        setItem({ itemKey: "userTenancy", userId }, {
            lastProjectId: projectId,
            lastOrganizationId: organizationId
        })
        window.location.reload();
    } else if (type === "TENANCY:SET-WORKSPACE") {
        const { workspaceId } = payload;
        const userId = getState().auth.userId;

        setItem({ itemKey: "userWorkspaceId", userId }, workspaceId);
    } else if (type === "TENANCY:CREATE-ACCOUNT") {
        const { accountName } = payload;
        try {
            dispatch({
                type: "TENANCY:CREATE-ACCOUNT-PENDING"
            });
            const account = await apiClient.post(`${externals.tenancyBackend}/account/${accountName}`, {});
            const accountId = _.get(account, 'data.accountPublicId');
            dispatch({
                type: 'TENANCY:CREATE-ACCOUNT-SUCCESS',
                payload: { accountId }
            });
            dispatch({
                type: "TENANCY:SET-ACCOUNT",
                payload: { accountId }
            })
        } catch (e) {
            dispatch({
                type: "TENANCY:CREATE-ACCOUNT-FAILED",
                payload: e
            })
        }

    } else if (type === "TENANCY/CREATE-ORGANIZATION") {
        const { organizationName } = payload;

        return handleApiAction(action, () => {
            return apiClient.post(`${externals.platformBackend}/v1/NetApp.Tenancy/organizations`, {
                name: organizationName
            });
        }).then(() => {
            window.location.reload();
        }).catch(e => {
            dispatch({
                type: "NOTIFICATIONS:ERROR",
                payload: {
                    message: t("failed_to_create_organization",i18nNamespace),
                    exception: e,
                    group: "TENANCY/CREATE-ORGANIZATION"
                }
            })
        })

    } else if (type === "TENANCY:LOAD-ACCOUNTS-SUCCESS") {
        const search = querystring.parse(window.location.search);

        if (search?.redirectFrom) {
            setTimeout(() => {
                const selectedAccountId = getState().tenancy.selectedAccountId;

                if (selectedAccountId) {
                    const search = querystring.parse(window.location.search);

                    if (search.redirectFrom === "spot") {
                        if (!search.spotOrgId) {
                            console.warn("Redirect from spot, but missing spotOrgId");
                        } else {
                            const spotOrgId = search.spotOrgId;

                            import('modules/spot' /* webpackChunkName: "spot"*/).then(() => {
                                dispatch({
                                    type: "SPOT:ASSOCIATE-EXTERNAL-ORG",
                                    payload: {
                                        spotOrgId: spotOrgId,
                                        selectedAccountId
                                    }
                                })
                            })
                        }

                        delete search.spotOrgId;
                        delete search.redirectFrom;

                        const searchString = querystring.stringify(search);

                        history.replace(window.location.pathname + (searchString ? `?${searchString}` : ''));
                    }
                }
            });
        }
    } else if (type === 'TENANCYV4:FETCH-ORG-ADMINS') {
        const state = getState();
        const safeModeHeaders = getSafeModeHeaders(state.features?.byKey);
        handlePromiseAction(action, () => {
            return getCachedClient(apiClient , `${externals.tenancyV4Backend}/resources/${payload.selectedOrgId}/permissions/bxp:organization-delete/users?filter=userType eq 'user'&limit=1`,`fetch-org-admin-${payload.selectedOrgId}`, safeModeHeaders);
        }, ({response: {data: {count}}, networkResponse}) => {
            handleNetworkResponse(networkResponse, ({data: {count}}, {dispatch}) => {
                dispatch({
                    type: `${type}-SUCCESS`,
                    payload: {
                        data: count > 0
                    },
                })
            }, (error) => {
                dispatch({
                    type: `${type}-SUCCESS`,
                    payload: error
                })
            }, {dispatch})
            return count > 0
        }).catch((e) => {
            console.error(e);
        })
    } else if (type === 'TENANCYV4:TOGGLE-SAFE-MODE-FOR-ORG') {
         const { selectedOrgId, safeMode } = payload;
        dispatch({ type: "TENANCY:SET-ORGANIZATION-SAFE-MODE-PENDING" });

        const state = getState();
        const { user, selectedAccountId, selectedAgentId } = state.tenancy;

        const cacheKeysToEvict = [`user-list-${user?.auth0Id}`,
        `elevated-state-${selectedOrgId}-${user?.id}`,
        `connectors-list-${selectedAccountId}`,
        `agent-user-${selectedAgentId}`,
        `fetch-org-admin-${selectedOrgId}`];

        return handleApiAction(action, () => {
            return apiClient.patch(`${externals.tenancyV4Backend}/organizations/${selectedOrgId}`, {
                "safeMode": safeMode ? "true" : "false",
                "type": "application/vnd.netapp.bxp.resource",
                "version": "1.0"
            },
                { headers: { 
                    'Content-Type': 'application/vnd.netapp.bxp.organization+json',
                    'X-Cache-Control': `delete-cache-keys=${cacheKeysToEvict.join(',')}`
                } }
            );
        }).then(() => {
             dispatch({ type: "TENANCY:SET-ORGANIZATION-SAFE-MODE-COMPLETE" });
             return Promise.resolve({orgMode: safeMode});
        }).catch(e => {
            const errMessage = _.get(e, 'response.data.invalidParams[0].reason') ?? "Unknown";
            console.error(e);
            dispatch({ type: "TENANCY:SET-ORGANIZATION-SAFE-MODE-COMPLETE" });
            return Promise.reject({ errMessage });
        })
    } else if (type === 'TENANCYV4:TOGGLE-USER-ACCESS-SAFE-MODE') {
        const { selectedOrgId, selectedUserId, elevatedStateDesired } = payload;
        const mode = elevatedStateDesired ? READ_ONLY_STATE.ELEVATED : READ_ONLY_STATE.DOWNSCOPED;
        dispatch({ type: "TENANCYV4:SAFE-MODE-MODIFY-USER-ACCESS-PENDING" });

        const state = getState();
        const { user, selectedAccountId, selectedAgentId } = state.tenancy;

        const cacheKeysToEvict = [`user-list-${user?.auth0Id}`,
            `elevated-state-${selectedOrgId}-${user?.id}`,
            `connectors-list-${selectedAccountId}`,
            `agent-user-${selectedAgentId}`,
            `fetch-org-admin-${selectedOrgId}`];

        return handleApiAction(action, () => {
            return apiClient.patch(`${externals.tenancyV4Backend}/organizations/${selectedOrgId}/users/${selectedUserId}`, { "elevatedStateDesired": mode },
                { headers: { 
                    'Content-Type': 'application/vnd.netapp.bxp.organization+json' ,
                    'X-Cache-Control': `delete-cache-keys=${cacheKeysToEvict.join(',')}`
                } }
            );
        }).then(() => {
            dispatch({ type: "TENANCYV4:SAFE-MODE-MODIFY-USER-ACCESS-COMPLETE"});
            return Promise.resolve({ elevatedStateDesired })
        }).catch(e => {
            const errMessage = _.get(e, 'response.data.invalidParams[0].reason') ?? "Unknown";
            console.error(e);
            dispatch({ type: "TENANCYV4:SAFE-MODE-MODIFY-USER-ACCESS-COMPLETE" });
            return Promise.reject({ errMessage });
        })
    } else if (type === 'TENANCYV4:CHECK-IF-USER-ISADMIN') {
        const { selectedOrgId } = payload;
        const state = getState();
        const userId = state.tenancy.user?.id;
        const safeModeHeaders = getSafeModeHeaders(state.features);
        let isAdmin = false;
        try {
            const allRoles = await apiClient.get(`${externals.tenancyV4Backend}/ui/organizations/${selectedOrgId}/users/${userId}/uiroles?${maxLimit}`, safeModeHeaders);
            _.forEach(allRoles?.data?.items, (item) => _.forEach(item.tags, (tag) => isAdmin = tag["internal:bxp:adminRole"] === "true"));
        } catch (e) {
            console.error(" Excpetion fetching all user roles", e);
        }
        dispatch({
            type: 'TENANCYV4:USER-IS-ADMIN',
            payload: {
                isAdmin
            }
        });
    } else if (type === "TENANCYV4:GET-SERVICE-PERMISSIONS") {
        const { permissionTagForService } = payload;
        const state = getState();
        const userId = state.auth.userId;
        const organizationId = state.tenancy.selectedOrgId;
        const promise = handlePromiseAction(action, async () => {
            return apiClient.get(`${externals.tenancyV4Backend}/users/${userId}/resources/${organizationId}/permissions?filter=service_application eq '${permissionTagForService}'&limit=10000`);
        });
        promise.then((resonse) => {
            const rulesForSelectedScope = _.map(resonse?.data?.items, (item) => {
                return item?.rule
            });

            dispatch({
                type: 'TENANCYV4:GET-SERVICE-PERMISSIONS-SUCCESS',
                payload: {
                    rulesForSelectedScope,
                    permissionTagForService
                }
            });
        })
    }
    // end

    return next(action);
}